// src/components/workspace/costing/PricingProfileStatus.tsx
// Shows the status of the user's pricing profile configuration

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, AlertCircle, Circle, Users, Calculator, Package,
  ArrowRight, TrendingUp, Sparkles
} from 'lucide-react';

interface PricingStats {
  laborCategories: number;
  indirectRatesConfigured: boolean;
  odcCategories: number;
  proposalsWithPricing: number;
  proposalsNeedingPricing: number;
  averageWrapRate: number | null;
}

interface PricingProfileStatusProps {
  stats: PricingStats | null;
  onConfigureRates: () => void;
}

interface StatusItem {
  label: string;
  status: 'complete' | 'partial' | 'missing';
  value: string;
  icon: React.ReactNode;
  priority: 'high' | 'medium' | 'low';
}

export function PricingProfileStatus({ stats, onConfigureRates }: PricingProfileStatusProps) {
  const navigate = useNavigate();

  if (!stats) return null;

  const navigateToConfig = (type: string) => {
    switch (type) {
      case 'labor':
        navigate('/workspace/costing/labor');
        break;
      case 'indirect':
        navigate('/workspace/costing/indirect-rates');
        break;
      case 'odc':
        navigate('/workspace/costing/odc');
        break;
      default:
        onConfigureRates();
    }
  };

  const getStatusItems = (): StatusItem[] => {
    return [
      {
        label: 'Labor Categories',
        status: stats.laborCategories >= 5 ? 'complete' : stats.laborCategories > 0 ? 'partial' : 'missing',
        value: stats.laborCategories > 0
          ? `${stats.laborCategories} categories (${stats.laborCategories >= 5 ? 'Good coverage' : 'Add more for better proposals'})`
          : 'Not configured',
        icon: <Users className="h-4 w-4" />,
        priority: 'high'
      },
      {
        label: 'Indirect Rates',
        status: stats.indirectRatesConfigured ? 'complete' : 'missing',
        value: stats.indirectRatesConfigured
          ? `Configured (${stats.averageWrapRate?.toFixed(2)}x wrap rate)`
          : 'Required for accurate pricing',
        icon: <Calculator className="h-4 w-4" />,
        priority: 'high'
      },
      {
        label: 'ODC Categories',
        status: stats.odcCategories >= 3 ? 'complete' : stats.odcCategories > 0 ? 'partial' : 'missing',
        value: stats.odcCategories > 0
          ? `${stats.odcCategories} categories configured`
          : 'Optional - add common ODCs',
        icon: <Package className="h-4 w-4" />,
        priority: 'medium'
      }
    ];
  };

  const statusItems = getStatusItems();
  const completedCount = statusItems.filter(item => item.status === 'complete').length;
  const profileCompleteness = Math.round((completedCount / statusItems.length) * 100);

  const getStatusIcon = (status: StatusItem['status']) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'partial':
        return <AlertCircle className="h-5 w-5 text-fit-moderate" />;
      default:
        return <Circle className="h-5 w-5 text-gray-300" />;
    }
  };

  const getStatusColor = (status: StatusItem['status']) => {
    switch (status) {
      case 'complete':
        return 'bg-success-bg border-success-border';
      case 'partial':
        return 'bg-fit-moderate-bg border-fit-moderate-border';
      default:
        return 'bg-bw-surface-subtle border-bw-border';
    }
  };

  const getPriorityBadge = (priority: StatusItem['priority']) => {
    switch (priority) {
      case 'high':
        return <span className="px-1.5 py-0.5 bg-urgent-bg text-urgent text-xs rounded">Required</span>;
      case 'medium':
        return <span className="px-1.5 py-0.5 bg-fit-moderate-bg text-fit-moderate text-xs rounded">Recommended</span>;
      default:
        return <span className="px-1.5 py-0.5 bg-bw-surface-subtle text-bw-muted text-xs rounded">Optional</span>;
    }
  };

  return (
    <div className="bg-bw-surface rounded-xl border border-bw-border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-semibold text-bw-ink flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-bw-primary" />
            Pricing Profile Status
          </h3>
          <p className="text-sm text-bw-muted mt-1">
            Configure your pricing profile to generate accurate proposals
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Completeness Ring */}
          <div className="relative w-16 h-16">
            <svg className="w-16 h-16 transform -rotate-90">
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                className="text-gray-200"
              />
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                strokeDasharray={`${profileCompleteness * 1.76} 176`}
                className={profileCompleteness === 100 ? 'text-success' : 'text-bw-primary'}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-semibold">{profileCompleteness}%</span>
            </div>
          </div>

          {profileCompleteness < 100 && (
            <button
              onClick={onConfigureRates}
              className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors text-sm"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Complete Setup
            </button>
          )}
        </div>
      </div>

      {/* Status Items */}
      <div className="grid grid-cols-3 gap-4">
        {statusItems.map((item, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border ${getStatusColor(item.status)} transition-colors`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center">
                <span className={`mr-2 ${
                  item.status === 'complete' ? 'text-success' :
                  item.status === 'partial' ? 'text-fit-moderate' : 'text-bw-muted'
                }`}>
                  {item.icon}
                </span>
                <span className="font-medium text-bw-ink">{item.label}</span>
              </div>
              {getStatusIcon(item.status)}
            </div>

            <p className="text-sm text-bw-muted mb-2">{item.value}</p>

            <div className="flex items-center justify-between">
              {item.status !== 'complete' && getPriorityBadge(item.priority)}
              {item.status !== 'complete' && (
                <button
                  onClick={() => navigateToConfig(
                    item.label === 'Labor Categories' ? 'labor' :
                    item.label === 'Indirect Rates' ? 'indirect' : 'odc'
                  )}
                  className="text-xs text-bw-primary hover:text-bw-primary flex items-center"
                >
                  Configure <ArrowRight className="h-3 w-3 ml-1" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* AI Recommendation */}
      {profileCompleteness < 100 && (
        <div className="mt-4 p-3 bg-bw-primary-tint border border-bw-primary/20 rounded-lg">
          <div className="flex items-start">
            <Sparkles className="h-4 w-4 text-bw-primary mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="text-sm text-bw-primary font-medium">AI Recommendation</p>
              <p className="text-sm text-bw-primary mt-1">
                {!stats.indirectRatesConfigured
                  ? 'Start with indirect rates - they\'re essential for calculating competitive wrap rates. Most federal contractors use Fringe (25-35%), Overhead (40-100%), G&A (10-20%), and Profit (10-15%).'
                  : stats.laborCategories < 5
                  ? 'Add more labor categories to cover common roles. AI can suggest GSA-aligned rates for your industry.'
                  : 'Consider adding ODC categories for travel, equipment, and materials that are common in your contracts.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
